/**
 * RateTableAdminPage · v2.7.65+E144 (2026-02-03)
 * ══════════════════════════════════════════════════════════════════════
 * GP-only editor for the New-Trip pricing engine's rate table.
 * Every static preset is listed; a GP can override any field or hide
 * the preset from the driver dropdown. Overrides are per-key documents
 * in `pricing_rate_overrides`, so wiping one row instantly reverts it
 * to the baked-in static value.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import adminApi from "@/lib/adminApi";
import { Loader2, Save, RotateCcw, EyeOff, Eye, ShieldAlert, Pencil, Upload, Download } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";


// v2.7.65+E146 · Phase 3.5 · Minimal CSV parser — expects the first line
// to be a header row with any subset of the supported columns. Skips
// blank rows and trims whitespace.
const CSV_FIELDS = ["key", "label", "base_fare", "extra_rider", "flat_per_passenger", "in_city_discount_pct", "hidden"];

function parseCsv(text) {
  const lines = text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean);
  if (lines.length === 0) return { rows: [], header: [] };
  const header = lines[0].split(",").map((c) => c.trim().toLowerCase());
  const rows = [];
  for (let i = 1; i < lines.length; i += 1) {
    const cells = lines[i].split(",").map((c) => c.trim());
    const row = {};
    header.forEach((h, idx) => {
      if (!CSV_FIELDS.includes(h)) return;
      const val = cells[idx];
      if (val === undefined || val === "") return;
      if (h === "hidden") row[h] = /^(1|true|yes)$/i.test(val);
      else if (h === "key" || h === "label") row[h] = val;
      else row[h] = parseFloat(val);
    });
    if (row.key) rows.push(row);
  }
  return { rows, header };
}


function BulkImportCard({ onImported }) {
  const [busy,    setBusy]    = useState(false);
  const [preview, setPreview] = useState(null);
  const [error,   setError]   = useState("");

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 200_000) { setError("CSV too large (max 200 KB)"); return; }
    setError("");
    try {
      const text = await file.text();
      const parsed = parseCsv(text);
      if (parsed.rows.length === 0) { setError("No usable rows found in CSV"); return; }
      setPreview(parsed);
    } catch (err) {
      setError("Failed to read CSV: " + err.message);
    }
  };

  const apply = async () => {
    if (!preview?.rows?.length) return;
    setBusy(true);
    try {
      const { data } = await adminApi.post(
        "/pricing/new-trip/admin/rate-table/bulk-import",
        { rows: preview.rows },
      );
      const applied  = data.applied_count || 0;
      const rejected = data.rejected_count || 0;
      toast.success(`Applied ${applied} override${applied === 1 ? "" : "s"}` + (rejected ? ` · ${rejected} rejected` : ""));
      if (rejected > 0) {
        console.warn("[rate-editor] rejected rows:", data.rejected);
      }
      setPreview(null);
      onImported?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk import failed");
    } finally { setBusy(false); }
  };

  const downloadTemplate = () => {
    const csv = [
      "key,label,base_fare,extra_rider,flat_per_passenger,in_city_discount_pct,hidden",
      "hydro,Hydro,25.00,10.00,,,false",
      "clinton,Clinton,35.00,10.00,,,false",
      "weatherford-in-city,Weatherford (in-city),,,11.11,10,false",
    ].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "rate-table-template.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Card className="p-4 space-y-3" data-testid="rate-bulk-import-card">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="text-[10px] uppercase tracking-widest text-amber-400 font-bold flex items-center gap-1">
          <Upload className="h-3 w-3"/>Bulk import (CSV)
        </div>
        <Button
          size="sm" variant="ghost"
          onClick={downloadTemplate}
          data-testid="rate-bulk-download-template"
          className="text-xs text-zinc-400 hover:text-zinc-100"
        >
          <Download className="h-3 w-3 mr-1"/>Template
        </Button>
      </div>
      <label
        className="w-full flex items-center justify-center h-14 rounded-lg border-2 border-dashed border-zinc-700 hover:border-amber-400/60 cursor-pointer text-xs text-zinc-400 hover:text-zinc-100"
        data-testid="rate-bulk-drop"
      >
        <input type="file" accept=".csv,text/csv" className="hidden" onChange={onFile}/>
        Drop or click to upload a CSV of overrides
      </label>
      {error && (
        <div className="text-xs text-rose-400" data-testid="rate-bulk-error">{error}</div>
      )}
      {preview && (
        <div className="space-y-2" data-testid="rate-bulk-preview">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">
            Preview · {preview.rows.length} row{preview.rows.length === 1 ? "" : "s"}
          </div>
          <div className="max-h-40 overflow-auto rounded border border-zinc-800 text-[10px] font-mono">
            <table className="w-full">
              <thead className="bg-zinc-900 text-zinc-500 sticky top-0">
                <tr>{Object.keys(preview.rows[0]).map((h) => <th key={h} className="text-left px-2 py-1">{h}</th>)}</tr>
              </thead>
              <tbody>
                {preview.rows.slice(0, 20).map((r, i) => (
                  <tr key={i} className="border-t border-zinc-800">
                    {Object.keys(preview.rows[0]).map((h) => <td key={h} className="px-2 py-1 text-zinc-300">{String(r[h] ?? "")}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
            {preview.rows.length > 20 && (
              <div className="text-center text-[10px] text-zinc-600 py-1">…{preview.rows.length - 20} more</div>
            )}
          </div>
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="ghost" onClick={() => setPreview(null)} data-testid="rate-bulk-cancel" className="text-zinc-400">
              Cancel
            </Button>
            <Button size="sm" onClick={apply} disabled={busy} data-testid="rate-bulk-apply" className="bg-amber-500 hover:bg-amber-400 text-black font-bold">
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1"/> : <Upload className="h-3.5 w-3.5 mr-1"/>}
              Apply {preview.rows.length}
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}


const TIER_BADGE = {
  1: { label: "Tier 1 · In-city",       cls: "bg-emerald-500/20 text-emerald-300 border-emerald-400/40" },
  2: { label: "Tier 2 · Regional",      cls: "bg-sky-500/20 text-sky-300 border-sky-400/40" },
  3: { label: "Tier 3 · Long-distance", cls: "bg-fuchsia-500/20 text-fuchsia-300 border-fuchsia-400/40" },
};


function RateRow({ row, onSaved }) {
  const staticVals = row.static;
  const override = row.override || {};

  // Pre-populate the form with either the override (if set) or the static baseline.
  const [label,     setLabel]     = useState(override.label ?? staticVals.label);
  const [baseFare,  setBaseFare]  = useState(override.base_fare ?? staticVals.base_fare);
  const [extra,     setExtra]     = useState(override.extra_rider ?? staticVals.extra_rider);
  const [flat,      setFlat]      = useState(override.flat_per_passenger ?? staticVals.flat_per_passenger);
  const [discount,  setDiscount]  = useState(override.in_city_discount_pct ?? staticVals.in_city_discount_pct);
  const [hidden,    setHidden]    = useState(!!override.hidden);
  const [saving,    setSaving]    = useState(false);
  const [resetting, setResetting] = useState(false);

  const dirty = useMemo(() => (
    label !== (override.label ?? staticVals.label)
    || baseFare !== (override.base_fare ?? staticVals.base_fare)
    || extra    !== (override.extra_rider ?? staticVals.extra_rider)
    || flat     !== (override.flat_per_passenger ?? staticVals.flat_per_passenger)
    || discount !== (override.in_city_discount_pct ?? staticVals.in_city_discount_pct)
    || hidden   !== !!override.hidden
  ), [label, baseFare, extra, flat, discount, hidden, override, staticVals]);

  const save = async () => {
    setSaving(true);
    try {
      const payload = {
        label:                label !== staticVals.label ? label : null,
        base_fare:            baseFare !== staticVals.base_fare ? Number(baseFare) : null,
        extra_rider:          extra    !== staticVals.extra_rider ? Number(extra) : null,
        flat_per_passenger:   flat     !== staticVals.flat_per_passenger ? Number(flat) : null,
        in_city_discount_pct: discount !== staticVals.in_city_discount_pct ? Number(discount) : null,
        hidden,
      };
      await adminApi.put(`/pricing/new-trip/admin/rate-table/${row.key}`, payload);
      toast.success(`${label} updated`);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const reset = async () => {
    if (!window.confirm(`Reset ${row.key} to the baked-in static defaults?`)) return;
    setResetting(true);
    try {
      await adminApi.delete(`/pricing/new-trip/admin/rate-table/${row.key}`);
      toast.success(`${row.key} reset to defaults`);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reset failed");
    } finally { setResetting(false); }
  };

  const tier = TIER_BADGE[staticVals.tier];
  const hasOverride = Object.entries(override).some(
    ([k, v]) => v !== null && v !== undefined && !["updated_at", "updated_by"].includes(k) && v !== false
  );

  return (
    <Card className={`p-4 space-y-3 ${hidden ? "opacity-60" : ""}`} data-testid={`rate-row-${row.key}`}>
      <div className="flex items-start justify-between gap-2 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">{row.key}</span>
            {tier && (
              <span className={`text-[9px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full border ${tier.cls}`}>
                {tier.label}
              </span>
            )}
            {hasOverride && (
              <span className="text-[9px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-400/40" data-testid={`rate-row-${row.key}-override-badge`}>
                Overridden
              </span>
            )}
            {hidden && (
              <span className="text-[9px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full bg-rose-500/20 text-rose-300 border border-rose-400/40">
                Hidden
              </span>
            )}
          </div>
          <div className="text-sm font-bold text-zinc-100 mt-1">{label || row.key}</div>
        </div>
        <Button
          variant="ghost" size="sm"
          onClick={reset}
          disabled={resetting || !hasOverride}
          data-testid={`rate-row-${row.key}-reset`}
          className="text-zinc-400 hover:text-rose-300 disabled:opacity-30"
          title="Reset to static defaults"
        >
          {resetting ? <Loader2 className="h-3.5 w-3.5 animate-spin"/> : <RotateCcw className="h-3.5 w-3.5"/>}
        </Button>
      </div>

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <div>
          <label className="text-[9px] uppercase tracking-widest text-zinc-500 font-bold block mb-1">Label</label>
          <Input
            value={label ?? ""} onChange={(e) => setLabel(e.target.value)}
            data-testid={`rate-row-${row.key}-label`} className="h-8 text-sm"
          />
        </div>
        {staticVals.tier === 1 ? (
          <>
            <div>
              <label className="text-[9px] uppercase tracking-widest text-zinc-500 font-bold block mb-1">$/passenger</label>
              <Input
                type="number" step="0.01"
                value={flat ?? 0} onChange={(e) => setFlat(parseFloat(e.target.value) || 0)}
                data-testid={`rate-row-${row.key}-flat`} className="h-8 text-sm"
              />
            </div>
            <div>
              <label className="text-[9px] uppercase tracking-widest text-zinc-500 font-bold block mb-1">Discount %</label>
              <Input
                type="number" step="0.1"
                value={discount ?? 0} onChange={(e) => setDiscount(parseFloat(e.target.value) || 0)}
                data-testid={`rate-row-${row.key}-discount`} className="h-8 text-sm"
              />
            </div>
          </>
        ) : (
          <>
            <div>
              <label className="text-[9px] uppercase tracking-widest text-zinc-500 font-bold block mb-1">Base fare</label>
              <Input
                type="number" step="0.5"
                value={baseFare ?? 0} onChange={(e) => setBaseFare(parseFloat(e.target.value) || 0)}
                data-testid={`rate-row-${row.key}-base`} className="h-8 text-sm"
              />
            </div>
            <div>
              <label className="text-[9px] uppercase tracking-widest text-zinc-500 font-bold block mb-1">Extra rider</label>
              <Input
                type="number" step="0.5"
                value={extra ?? 0} onChange={(e) => setExtra(parseFloat(e.target.value) || 0)}
                data-testid={`rate-row-${row.key}-extra`} className="h-8 text-sm"
              />
            </div>
          </>
        )}
        <div>
          <label className="text-[9px] uppercase tracking-widest text-zinc-500 font-bold block mb-1">Visibility</label>
          <Button
            variant="outline" size="sm"
            onClick={() => setHidden((v) => !v)}
            data-testid={`rate-row-${row.key}-toggle-hidden`}
            className={`h-8 w-full ${hidden ? "border-rose-400/60 text-rose-300" : ""}`}
          >
            {hidden ? <><EyeOff className="h-3.5 w-3.5 mr-1"/>Hidden</> : <><Eye className="h-3.5 w-3.5 mr-1"/>Visible</>}
          </Button>
        </div>
      </div>

      <div className="flex items-center justify-between gap-2 pt-1 border-t border-zinc-800">
        <div className="text-[10px] text-zinc-500">
          {override.updated_at ? (
            <>Last edited by <span className="font-bold text-zinc-300">{override.updated_by || "?"}</span> · {new Date(override.updated_at).toLocaleString()}</>
          ) : "No overrides yet"}
        </div>
        <Button
          size="sm"
          onClick={save}
          disabled={saving || !dirty}
          data-testid={`rate-row-${row.key}-save`}
          className="h-8 bg-amber-500 hover:bg-amber-400 text-black font-bold disabled:opacity-40"
        >
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1"/> : <Save className="h-3.5 w-3.5 mr-1"/>}
          Save
        </Button>
      </div>
    </Card>
  );
}


export default function RateTableAdminPage() {
  const [rows,    setRows]    = useState([]);
  const [loading, setLoading] = useState(true);
  const [gate,    setGate]    = useState(true);   // true = allowed, false = 403

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get("/pricing/new-trip/admin/rate-table");
      setRows(data.rows || []);
      setGate(true);
    } catch (e) {
      if (e?.response?.status === 403) { setGate(false); }
      else toast.error("Failed to load rate table");
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return <div className="p-8 text-zinc-400 flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin"/>Loading rate table…</div>;
  }
  if (!gate) {
    return (
      <div className="p-8 max-w-md mx-auto text-center" data-testid="rate-table-403">
        <ShieldAlert className="h-12 w-12 text-rose-400 mx-auto mb-3"/>
        <div className="text-lg font-bold text-rose-300 mb-1">General Partners only</div>
        <div className="text-sm text-zinc-500">Only General Partners can edit the New-Trip rate table.</div>
      </div>
    );
  }

  const grouped = { 1: [], 2: [], 3: [] };
  rows.forEach((r) => {
    (grouped[r.static.tier] || grouped[3]).push(r);
  });

  return (
    <div className="space-y-4 p-4 max-w-5xl mx-auto" data-testid="rate-table-admin-page">
      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] text-amber-400 font-bold flex items-center gap-1.5">
          <Pencil className="h-3 w-3"/>New Trip Pricing
        </div>
        <h1 className="text-xl font-black text-zinc-100">Rate Table Editor</h1>
        <p className="text-xs text-zinc-500 mt-0.5">
          Every preset here powers the driver's New Trip dropdown. Blank fields keep the baked-in static defaults; overrides are saved to <span className="font-mono">pricing_rate_overrides</span> and take effect immediately.
        </p>
      </div>

      <BulkImportCard onImported={load}/>

      {[1, 2, 3].map((tier) => (
        <section key={tier} className="space-y-2" data-testid={`rate-tier-${tier}`}>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">
            {TIER_BADGE[tier].label} · {grouped[tier].length} preset{grouped[tier].length === 1 ? "" : "s"}
          </div>
          <div className="space-y-2">
            {grouped[tier].map((row) => (
              <RateRow key={row.key} row={row} onSaved={load}/>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
